import { getAnalyticsEvents } from './analyticsService';

export interface PerformanceMetrics {
  apiResponseTime: number;
  contentLoadTime: number;
  appStartupTime: number;
  screenLoadTime: number;
  errorRateByComponent: Map<string, number>;
  crashRate: number;
  firestoreReads: number;
  firestoreWrites: number;
  storageUsage: number;
  costPerOperation: Map<string, number>;
}

export interface PerformanceAnalytics {
  metrics: PerformanceMetrics;
  responseTimeHistory: Array<{ date: string; time: number }>;
  errorTrends: Array<{ date: string; errors: number }>;
}

export const getPerformanceAnalytics = async (
  days: number = 30
): Promise<PerformanceAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days);
    
    const performanceEvents = events.filter((e) =>
      e.category === 'performance' || e.eventName === 'performance_metric' ||
      e.eventName.includes('performance') || e.eventName.includes('response_time')
    );

    const errorEvents = events.filter((e) =>
      e.category === 'error' || e.eventName.includes('error')
    );

    const errorByComponent = new Map<string, number>();
    errorEvents.forEach((event) => {
      const component = (event.properties.component as string) ||
                       (event.properties.source as string) ||
                       'unknown';
      errorByComponent.set(component, (errorByComponent.get(component) || 0) + 1);
    });

    const apiTimings: number[] = [];
    const contentTimings: number[] = [];
    const startupTimings: number[] = [];
    const screenTimings: number[] = [];

    performanceEvents.forEach((event) => {
      const metricType = (event.properties.metric_type as string) || '';
      const duration = (event.properties.duration_ms as number) || 0;
      if (!duration) return;

      if (metricType === 'api_response' || event.eventName.includes('api_response')) {
        apiTimings.push(duration);
      } else if (metricType === 'content_load' || event.eventName.includes('content_load')) {
        contentTimings.push(duration);
      } else if (metricType === 'app_startup' || event.eventName.includes('app_startup')) {
        startupTimings.push(duration);
      } else if (metricType === 'screen_load' || event.eventName.includes('screen_load')) {
        screenTimings.push(duration);
      }
    });

    const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    const crashRate = events.length > 0
      ? (errorEvents.filter((e) => e.properties.fatal === true).length / events.length) * 100
      : 0;

    const firestoreReads = events.length;
    const firestoreWrites = events.filter((e) => e.category === 'user_action').length;
    const storageUsage = 0;

    const costPerOperation = new Map<string, number>();
    costPerOperation.set('firestore_read', (firestoreReads / 100000) * 0.06);
    costPerOperation.set('firestore_write', (firestoreWrites / 100000) * 0.18);
    costPerOperation.set('storage', (storageUsage / 1024) * 0.18);

    const metrics: PerformanceMetrics = {
      apiResponseTime: Math.round(avg(apiTimings)),
      contentLoadTime: Math.round(avg(contentTimings)),
      appStartupTime: Math.round(avg(startupTimings)),
      screenLoadTime: Math.round(avg(screenTimings)),
      errorRateByComponent: errorByComponent,
      crashRate,
      firestoreReads,
      firestoreWrites,
      storageUsage,
      costPerOperation,
    };

    const responseTimeByDate = new Map<string, { sum: number; count: number }>();
    performanceEvents.forEach((event) => {
      const duration = (event.properties.duration_ms as number) || 0;
      if (!duration) return;
      const dateKey = event.timestamp.toISOString().split('T')[0];
      const entry = responseTimeByDate.get(dateKey) || { sum: 0, count: 0 };
      entry.sum += duration;
      entry.count++;
      responseTimeByDate.set(dateKey, entry);
    });

    const responseTimeHistory = Array.from(responseTimeByDate.entries())
      .map(([date, data]) => ({ date, time: Math.round(data.sum / data.count) }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const errorsByDate = new Map<string, number>();
    errorEvents.forEach((event) => {
      const dateKey = event.timestamp.toISOString().split('T')[0];
      errorsByDate.set(dateKey, (errorsByDate.get(dateKey) || 0) + 1);
    });

    const errorTrends = Array.from(errorsByDate.entries())
      .map(([date, errors]) => ({ date, errors }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      metrics,
      responseTimeHistory,
      errorTrends,
    };
  } catch (error) {
    console.error('Error calculating performance analytics:', error);
    throw error;
  }
};
